import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Dropdown,
  Button,
  ButtonGroup,
  Form,
  Modal,
} from "react-bootstrap";
import { MdCalendarMonth } from "react-icons/md";
import "./month-report.css";
import WorkReportLayout from "../../components/EmpWorkReportLayout.jsx";
import { IoEye } from "react-icons/io5";
import axios from '../../Authservices/axiosInstance';
import moment from "moment"; // Import moment.js for date formatting

export default function MonthReport() {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10); // Default entries per page
  const [currentPage, setCurrentPage] = useState(1); // Current page state

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    // Function to fetch reports for the current month
    const fetchReports = async () => {
      try {
        const response = await axios.get(
          "https://backend.ems.uk.hiradentallab.com/api/reports/this-month"
        );
        setReports(response.data.reports);
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, []); // Empty dependency array means this runs only once when the component mounts

  // Function to handle search query change and fetch results
  const handleSearchChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    try {
      // Send a request to dynamically search reports for the current month
      const response = await axios.post(
        "https://backend.ems.uk.hiradentallab.com/api/reports/months/search",
        { query }
      );
      setReports(response.data.reports); // Update the reports with search results
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const filteredReports = reports.filter((report) => {
    return (
      report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.reportingDate.includes(searchQuery)
    );
  });
  // Modal related handlers
  const handleOpenModal = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  // Helper function to format date
  const formatDate = (date) => {
    return moment(date).format("MM/DD/YYYY");
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredReports.length / entriesPerPage);

  const pageRange = () => {
    const range = [];
    for (let i = 1; i <= totalPages; i++) {
      range.push(i);
    }
    return range;
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <WorkReportLayout>
      <div>
        <h1 className="month-report-header">
          <MdCalendarMonth className="month-report-icon" />
          Current Month Work Report
        </h1>
        <hr />
        <div className="month-report-content">
          <div>
            <Row>
              <Col xs={12} md={6} className="d-flex align-items-center mb-2">
                <Dropdown as={ButtonGroup}>
                  <Button variant="primary">Show Entries</Button>
                  <Dropdown.Toggle
                    split
                    variant="primary"
                    id="dropdown-split-basic"
                  />
                  <Dropdown.Menu>
                    {[10, 25, 50, 100].map((size) => (
                      <Dropdown.Item
                        key={size}
                        onClick={() => setEntriesPerPage(size)}
                      >
                        {size}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col
                xs={12}
                md={6}
                className="d-flex justify-content-end mb-3 search-container"
              >
                <Form className="d-flex">
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form>
              </Col>
            </Row>

            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="table-header">
                  <tr className="month-report-table-header">
                    <th>SR</th>
                    <th>Name</th>
                    <th>UserName</th>
                    <th>Reporting Date</th>
                    <th>Reporting Time</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7">Loading...</td>
                    </tr>
                  ) : (
                    filteredReports
                      .slice(
                        (currentPage - 1) * entriesPerPage,
                        currentPage * entriesPerPage
                      )
                      .map((report, index) => (
                        <tr className="table-row" key={report.reportId}>
                          <td>
                            {(currentPage - 1) * entriesPerPage + index + 1}
                          </td>
                          <td>{report.name}</td>
                          <td>{report.username}</td>
                          <td>
                            {moment(report.reportingDate).format("MM/DD/YYYY")}
                          </td>{" "}
                          {/* Format date */}
                          <td>
                            {moment(report.reportingTime, "HH:mm").format(
                              "h:mm A"
                            )}
                          </td>{" "}
                          {/* Format time */}
                          <td>
                            {moment(report.updatedAt).format("MM/DD/YYYY")}
                          </td>{" "}
                          {/* Format updated date */}
                          <td>
                            <div className="action-buttons">
                              <Button
                                className="btn btn-primary"
                                onClick={() => handleOpenModal(report)}
                                size="sm"
                              >
                                <IoEye />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
                <tfoot className="table-footer">
                  <tr className="month-report-table-footer">
                    <td>Total</td>
                    <td>{filteredReports.length}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              <div className="pagination-row">
                <div className="entries-info">
                  Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * entriesPerPage,
                    filteredReports.length
                  )}{" "}
                  of {filteredReports.length} entries
                </div>
                <nav>
                  <ul className="pagination no-gap">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageRange().map((pageNumber) => (
                      <li
                        key={pageNumber}
                        className={`page-item ${
                          currentPage === pageNumber ? "active" : ""
                        }`}
                      >
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(pageNumber);
                          }}
                        >
                          {pageNumber}
                        </a>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Modal for report details */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header
            closeButton
            style={{ background: "#004aad", color: "#ffffff" }}
          >
            <Modal.Title>Report Details</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "20px" }}>
            {selectedReport && (
              <div>
                <h6 style={{ fontWeight: "bold" }}>
                  Start Date:{" "}
                  <span style={{ fontStyle: "italic", color: "#007bff" }}>
                    {formatDate(selectedReport.startDate)}
                  </span>
                </h6>
                <h6 style={{ fontWeight: "bold" }}>
                  Reporting Time:{" "}
                  <span style={{ fontStyle: "italic", color: "#007bff" }}>
                    {selectedReport.reportingTime}
                  </span>
                </h6>
                <div
                  style={{
                    border: "1px solid #007bff",
                    padding: "10px",
                    borderRadius: "5px",
                    backgroundColor: "#f8f9fa",
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedReport.report }}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </WorkReportLayout>
  );
}
